import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CommonApiResponseType } from 'src/models/BaseApi';
import { AnyRequestBaseStoreEntityType } from 'src/store/types';
import { SafeAnyType } from 'src/utils/safeAny';

export type AnyRequestActionType<T> = {
  data: T;
  apiFunction: (data: T) => Promise<SafeAnyType>;
  uniqueId: string;
  callerName?: string;
};

export type AnyRequestSliceStateType = (AnyRequestBaseStoreEntityType & { internalId: number })[];

type InternalIdType = AnyRequestSliceStateType[0]['internalId'];
type UniqueIdType = AnyRequestSliceStateType[0]['uniqueId'];

const initialState: AnyRequestSliceStateType = [];

const callIfFound =
  (state: AnyRequestSliceStateType, uniqueId: UniqueIdType, internalId: InternalIdType) =>
  (fn: (foundIndex: number) => void) => {
    const foundIndex = state.findIndex((item) => item.uniqueId === uniqueId && item.internalId === internalId);

    if (~foundIndex) {
      fn(foundIndex);
    }
  };

/**
 * a stack of any requests running in the app (e.g. to save something unimportant)
 * you wish to not save in store as a specific reducer
 *
 * apiFunction is a non-serializable value (keep this in mind)
 */
export const anyRequestSlice = createSlice({
  name: 'anyRequest',
  initialState,
  reducers: {
    start: (state, { payload: { uniqueId } }: PayloadAction<AnyRequestActionType<SafeAnyType>>) => {
      state.push({
        loading: true,
        response: null,
        error: null,
        uniqueId,
        internalId: 0,
      });
    },
    setInternalId: (
      state,
      { payload: { uniqueId, internalId } }: PayloadAction<{ uniqueId: UniqueIdType; internalId: InternalIdType }>
    ) => {
      callIfFound(
        state,
        uniqueId,
        0
      )((foundIndex) => {
        state[foundIndex] = {
          ...state[foundIndex],
          internalId,
        };
      });
    },
    ok: (
      state,
      {
        payload: { data, uniqueId, internalId },
      }: PayloadAction<{ data: SafeAnyType; uniqueId: UniqueIdType; internalId: InternalIdType }>
    ) => {
      callIfFound(
        state,
        uniqueId,
        internalId
      )((foundIndex) => {
        state[foundIndex] = {
          ...state[foundIndex],
          loading: false,
          response: data,
        };
      });
    },
    clear: (state, { payload: uniqueId }: PayloadAction<UniqueIdType>) =>
      state.filter((item) => item.uniqueId !== uniqueId),
    error: (
      state,
      {
        payload: { error, uniqueId, internalId, data },
      }: PayloadAction<{
        error: CommonApiResponseType;
        uniqueId: UniqueIdType;
        internalId: InternalIdType;
        data: SafeAnyType;
      }>
    ) => {
      callIfFound(
        state,
        uniqueId,
        internalId
      )((foundIndex) => {
        state[foundIndex] = {
          ...state[foundIndex],
          loading: false,
          response: data || null,
          error,
        };
      });
    },
  },
});

export const { start: anyRequestStart, clear: anyRequestClear } = anyRequestSlice.actions;
